import { useEffect, useRef } from 'react';

/**
 * 左侧视频字幕 Canvas：仅在 rvfc/rAF 内绘制，不依赖 render 回调引用重启循环。
 * renderSubtitleToCanvas 的更新通过 ref 同步，避免父组件重渲染时取消/重建 VideoFrameCallback。
 */
export function useSubtitleRenderer(
    videoRef: React.RefObject<HTMLVideoElement | null>,
    renderSubtitleToCanvas: (time: number) => void
): void {
    const renderRef = useRef(renderSubtitleToCanvas);
    renderRef.current = renderSubtitleToCanvas;

    const lastSubtitleTimeRef = useRef<number>(0);
    const rvfcIdRef = useRef<number | null>(null);
    const rafIdRef = useRef<number | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const renderFrame = () => {
            const currentTime = video.currentTime || 0;
            if (Math.abs(currentTime - lastSubtitleTimeRef.current) > 0.1) {
                renderRef.current(currentTime);
                lastSubtitleTimeRef.current = currentTime;
            }
        };

        const startRender = () => {
            const anyVideo = video as any;
            if (typeof anyVideo.requestVideoFrameCallback === 'function') {
                const cb = (_now: number, _metadata: any) => {
                    renderFrame();
                    rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                };
                rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                return;
            }
            const loopRAF = () => {
                renderFrame();
                rafIdRef.current = window.requestAnimationFrame(loopRAF);
            };
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const stopRender = () => {
            if (rvfcIdRef.current != null) {
                const anyVideo2 = video as any;
                if (typeof anyVideo2.cancelVideoFrameCallback === 'function') {
                    anyVideo2.cancelVideoFrameCallback(rvfcIdRef.current);
                }
                rvfcIdRef.current = null;
            }
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };

        const onPlay = () => startRender();
        const onPause = () => stopRender();
        const onEnded = () => stopRender();

        if (!video.paused) startRender();
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);

        return () => {
            stopRender();
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
        };
    }, [videoRef]);
}
